import { useCallback } from "react";
import { useNavigate } from "react-router";

import {
  ContactDetailsForm,
  MailingAddressForm,
  PolicySummary,
} from "@covertree/new-business-policy-summary";
import {
  saveInternalTrackingSubcode,
  setShowMailingAddressReminder,
  updatePhysicallyMailDocuments,
  updatePolicyholderDetails,
} from "@covertree/new-business-redux";
import { NewPolicyRoutes, navigateToPolicyStep } from "@routing/paths";
import { useAppDispatch } from "@redux/hooks";
import { featureFlags } from "@utils/featureFlags";

export function PolicySummaryStep() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const onContactDetailsSubmit = useCallback(
    async ({
      emailAddress,
      noEmail,
      primaryContactNumber,
      secondaryContactNumber,
      physicallyMailDocuments,
    }: ContactDetailsForm) => {
      await dispatch(
        updatePolicyholderDetails({
          emailAddress,
          noEmail,
          primaryContactNumber,
          secondaryContactNumber: secondaryContactNumber || null,
        })
      ).unwrap();

      if (physicallyMailDocuments !== undefined) {
        await dispatch(
          updatePhysicallyMailDocuments(physicallyMailDocuments)
        ).unwrap();
      }
    },
    [dispatch]
  );

  const onMailingAddressSubmit = useCallback(
    async ({ address, lotUnit }: MailingAddressForm) => {
      await dispatch(
        updatePolicyholderDetails({
          mailingAddress: {
            city: address.value.city,
            state: address.value.state,
            streetAddress: address.value.mainAddressLine,
            zipCode: address.value.postCode,
            ...(lotUnit || address.value.lotUnit
              ? { unitNumber: lotUnit || address.value.lotUnit }
              : {}),
          },
        })
      ).unwrap();
    },
    [dispatch]
  );

  const onCloseMailingAddressReminder = useCallback(() => {
    dispatch(setShowMailingAddressReminder(false));
  }, [dispatch]);

  const onContinue = useCallback(async () => {
    return navigateToPolicyStep(
      navigate,
      NewPolicyRoutes.CHECKOUT,
      NewPolicyRoutes.POLICY_SUMMARY
    );
  }, [dispatch]);

  const onEditMyPlan = useCallback(() => {
    return navigateToPolicyStep(
      navigate,
      NewPolicyRoutes.CUSTOMIZE_POLICY,
      NewPolicyRoutes.POLICY_SUMMARY
    );
  }, [navigate]);

  const onSaveInternalTrackingSubcode = useCallback(
    async (subcode: string) => {
      await dispatch(saveInternalTrackingSubcode({ subcode })).unwrap();
    },
    [dispatch]
  );

  return (
    <PolicySummary
      isD2C={false}
      useNewOffersAndSelectedQuotePricing={featureFlags.useNewOffersAndSelectedQuotePricing.isEnabled()}
      onCloseMailingAddressReminder={onCloseMailingAddressReminder}
      onContactDetailsSubmit={onContactDetailsSubmit}
      onContinue={onContinue}
      onEditMyPlan={onEditMyPlan}
      onMailingAddressSubmit={onMailingAddressSubmit}
      onSaveInternalTrackingSubcode={onSaveInternalTrackingSubcode}
    />
  );
}
